/**
 * analytics moduli uchun umumiy turlar/konstantalar va sof funksiyalar (API + veb + mobil).
 * Faqat analytics moduli egasi tahrirlaydi.
 *
 * Kunlar — "YYYY-MM-DD" biznes kuni (Asia/Tashkent). Barcha davrlar ikki uchi bilan (inclusive).
 */
import { addDays, businessDay } from '../format';
import type { Tone } from '../statuses';
import { LOW_STOCK_THRESHOLD } from './wms';
import type { Locale } from '../i18n';

// ------------------------------------------------------------------ Konstantalar
/** Bitta so'rovdagi eng uzun davr (kun). */
export const ANALYTICS_MAX_RANGE_DAYS = 366;
/** Rollup ishi har safar qayta hisoblaydigan oxirgi biznes kunlar soni. */
export const ROLLUP_RECENT_DAYS = 3;
/** Kuniga bir marta chuqur qayta hisoblash (kech yetkazish/qaytarishlar ham aks etsin). */
export const ROLLUP_DEEP_DAYS = 35;
/** Qoldiq yetish kunlari shu oyna bo'yicha sotuvdan hisoblanadi. */
export const COVER_WINDOW_DAYS = 14;
/** Shundan kam kunga yetadigan qoldiq — "kam qoldiq". */
export const LOW_COVER_DAYS = 7;
/** Shu donadan kam mavjud qoldiq — "kam qoldiq" (wms bilan bir xil chegara). */
export const LOW_STOCK_UNITS = LOW_STOCK_THRESHOLD;

export const GROUP_BYS = ['day', 'week', 'month'] as const;
export type GroupBy = (typeof GROUP_BYS)[number];
export const GROUP_BY_LABELS: Record<GroupBy, string> = { day: 'Kun', week: 'Hafta', month: 'Oy' };

export const PERIOD_PRESETS = [
  { value: 'today', label: 'Bugun' },
  { value: '7d', label: '7 kun' },
  { value: '30d', label: '30 kun' },
  { value: 'month', label: 'Oy' },
  { value: 'custom', label: 'Boshqa' },
] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number]['value'];

/** Oy qisqartmalari — grafik o'qlari uchun (to'liq nom joyga sig'maydi). */
const MONTHS: Record<Locale, readonly string[]> = {
  uz: ['Yan', 'Fev', 'Mar', 'Apr', 'May', 'Iyn', 'Iyl', 'Avg', 'Sen', 'Okt', 'Noy', 'Dek'],
  ru: ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  zh: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
};

// ------------------------------------------------------------------ Davrlar
export interface DayRange {
  from: string;
  to: string;
}

const dayMs = (d: string) => Date.parse(`${d}T00:00:00Z`);

/** Davrdagi kunlar soni (ikki uchi bilan): 2026-10-01..2026-10-07 → 7. */
export function spanDays(from: string, to: string): number {
  return Math.round((dayMs(to) - dayMs(from)) / 86_400_000) + 1;
}

/** Shu uzunlikdagi, darhol oldingi davr: 08..14 → 01..07. */
export function previousRange(from: string, to: string): DayRange {
  const n = spanDays(from, to);
  const prevTo = addDays(from, -1);
  return { from: addDays(prevTo, -(n - 1)), to: prevTo };
}

/** Tayyor davr → sanalar (bugungi biznes kuniga nisbatan). `month` — joriy oy boshidan bugungacha. */
export function presetRange(preset: string, today: string = businessDay()): DayRange {
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case '7d':
      return { from: addDays(today, -6), to: today };
    case 'month':
      return { from: `${today.slice(0, 7)}-01`, to: today };
    default:
      return { from: addDays(today, -29), to: today };
  }
}

/** Sanalar qaysi tayyor davrga mos (aks holda `custom`). */
export function detectPreset(from: string | undefined, to: string | undefined, today: string = businessDay()): PeriodPreset {
  if (!from || !to) return '30d';
  for (const p of ['today', '7d', '30d', 'month'] as const) {
    const r = presetRange(p, today);
    if (r.from === from && r.to === to) return p;
  }
  return 'custom';
}

// ------------------------------------------------------------------ O'zgarish
/** O'zgarish foizi (oldingi davrga). Oldingi 0 bo'lsa — solishtirib bo'lmaydi (null), ikkalasi 0 — 0. */
export function deltaPct(cur: number, prev: number): number | null {
  if (!Number.isFinite(cur) || !Number.isFinite(prev)) return null;
  if (prev === 0) return cur === 0 ? 0 : null;
  return Math.round(((cur - prev) / Math.abs(prev)) * 1000) / 10;
}

/** Ulushlar uchun o'zgarish — foiz punktlarida (12,5% → 14,0% = +1,5). */
export function deltaPoints(cur: number, prev: number): number {
  return Math.round((cur - prev) * 10) / 10;
}

/** Ulush foizi, 1 xona aniqlikda; maxraj 0 bo'lsa 0. */
export function sharePct(part: number, whole: number): number {
  if (!whole) return 0;
  return Math.round((part / whole) * 1000) / 10;
}

export function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

// ------------------------------------------------------------------ Guruhlash (kun / hafta / oy)
/**
 * Biznes kunini guruh boshiga keltirish: hafta — dushanbadan (ISO), oy — 1-sanadan.
 * Kirish allaqachon Toshkent biznes kuni ("YYYY-MM-DD") bo'lishi kerak.
 */
export function bucketStart(day: string, groupBy: GroupBy): string {
  if (groupBy === 'month') return `${day.slice(0, 7)}-01`;
  if (groupBy === 'week') {
    const dow = new Date(dayMs(day)).getUTCDay(); // 0 = yakshanba
    return addDays(day, -((dow + 6) % 7));
  }
  return day;
}

/** Vaqt nuqtasi (UTC) → Toshkent biznes kuni bo'yicha guruh boshi. */
export function bucketOf(at: Date | string | number, groupBy: GroupBy): string {
  return bucketStart(businessDay(at), groupBy);
}

/** Davrni qoplaydigan barcha guruh boshlari (bo'sh guruhlar ham — grafikda uzilish bo'lmasin). */
export function bucketKeys(from: string, to: string, groupBy: GroupBy): string[] {
  const out: string[] = [];
  let k = bucketStart(from, groupBy);
  const last = bucketStart(to, groupBy);
  while (k <= last) {
    out.push(k);
    if (groupBy === 'day') k = addDays(k, 1);
    else if (groupBy === 'week') k = addDays(k, 7);
    else {
      const y = Number(k.slice(0, 4));
      const m = Number(k.slice(5, 7));
      k = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
    }
  }
  return out;
}

/** Guruh nomi: kun "07.10", hafta "05.10–11.10", oy "Okt 2026" (oy nomi tilga qarab). */
export function bucketLabel(key: string, groupBy: GroupBy, locale: Locale = 'uz'): string {
  const dm = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}`;
  if (groupBy === 'month') return `${MONTHS[locale][Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}`;
  if (groupBy === 'week') return `${dm(key)}–${dm(addDays(key, 6))}`;
  return dm(key);
}

// ------------------------------------------------------------------ Qoldiq yetish kunlari
/**
 * Mavjud qoldiq necha kunga yetadi (oxirgi `windowDays` kunlik o'rtacha sotuv bo'yicha).
 * Qoldiq 0 — 0 kun; sotuv bo'lmasa — null (cheksiz, hisoblab bo'lmaydi).
 */
export function daysOfCover(available: number, soldUnits: number, windowDays: number = COVER_WINDOW_DAYS): number | null {
  if (available <= 0) return 0;
  if (soldUnits <= 0 || windowDays <= 0) return null;
  return Math.round((available / (soldUnits / windowDays)) * 10) / 10;
}

/** Kam qoldiq: mavjud ≤ chegara yoki yetish kunlari `LOW_COVER_DAYS` dan kam. */
export function isLowStock(available: number, cover: number | null): boolean {
  return available <= LOW_STOCK_UNITS || (cover !== null && cover < LOW_COVER_DAYS);
}

// ------------------------------------------------------------------ Persentil
/** Chiziqli interpolyatsiyali persentil (PostgreSQL `percentile_cont` kabi). p ∈ [0, 1]. */
export function percentile(values: readonly number[], p: number): number | null {
  const v = values.filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
  if (!v.length) return null;
  const pos = (v.length - 1) * Math.min(1, Math.max(0, p));
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return v[lo]! + (v[hi]! - v[lo]!) * (pos - lo);
}

export function median(values: readonly number[]): number | null {
  return percentile(values, 0.5);
}

/**
 * Gistogramma (qiymat → soni) bo'yicha persentil — katta hajmda xom qatorlarni yuklamaslik uchun
 * SQL qiymatlarni yaxlitlab guruhlaydi, persentil shu yerda topiladi (`percentile_disc` kabi).
 */
export function weightedPercentile(buckets: readonly { value: number; count: number }[], p: number): number | null {
  const b = buckets.filter((x) => x.count > 0 && Number.isFinite(x.value)).sort((a, c) => a.value - c.value);
  const total = b.reduce((s, x) => s + x.count, 0);
  if (!total) return null;
  const target = Math.max(1, Math.ceil(total * Math.min(1, Math.max(0, p))));
  let acc = 0;
  for (const x of b) {
    acc += x.count;
    if (acc >= target) return x.value;
  }
  return b[b.length - 1]!.value;
}

/** Bajarilish tezligi gistogrammasi oraliqlari (soat). */
export const SPEED_BINS = [
  { key: 'h2', label: '< 2 soat', max: 2 },
  { key: 'h6', label: '2–6 soat', max: 6 },
  { key: 'h12', label: '6–12 soat', max: 12 },
  { key: 'h24', label: '12–24 soat', max: 24 },
  { key: 'h48', label: '1–2 kun', max: 48 },
  { key: 'more', label: '2+ kun', max: Number.POSITIVE_INFINITY },
] as const;

export function speedBin(hours: number): (typeof SPEED_BINS)[number]['key'] {
  return (SPEED_BINS.find((b) => hours < b.max) ?? SPEED_BINS[SPEED_BINS.length - 1]!).key;
}

// ------------------------------------------------------------------ API javob turlari
export interface KpiValue {
  value: number;
  prev: number;
  /** Oldingi davrga o'zgarish: summalar — foizda, ulushlar — foiz punktida. null — solishtirib bo'lmaydi. */
  delta: number | null;
}

/**
 * Grafik qatorlari (`SalesTotals`, `DailyPoint`, `ChannelSlice`, `SalesPoint`) `interface` emas,
 * `type` bilan e'lon qilinadi: faqat shunda TypeScript yashirin indeks imzosini beradi va ular
 * `ChartCard` ning `table.rows` (`Record<string, unknown>[]`) parametriga castsiz uzatiladi.
 */
export type SalesTotals = {
  orders: number;
  gmv: number;
  units: number;
  delivered: number;
  deliveredAmount: number;
  canceled: number;
  returned: number;
};

export type DailyPoint = {
  day: string;
  orders: number;
  gmv: number;
  delivered: number;
  canceled: number;
};

export type ChannelSlice = {
  channel: string;
  label: string;
  orders: number;
  gmv: number;
};

export interface AttentionItem {
  key: string;
  /** Modul (ikonka va guruh uchun). */
  module: 'oms' | 'wms' | 'delivery' | 'catalog' | 'support' | 'finance' | 'hr' | 'integrations';
  label: string;
  count: number;
  to: string;
  tone: Tone;
}

export interface TopSeller {
  sellerId: string;
  name: string;
  logoUrl: string | null;
  orders: number;
  gmv: number;
  delivered: number;
  share: number;
}

export interface OverviewModules {
  wms: { queue: number; shortage: number; awaitingDispatch: number; pendingSupplies: number; packedToday: number; dispatchedToday: number } | null;
  delivery: { total: number; pending: number; onTheWay: number; delivered: number; deliverability: number; lost: number; cashCollected: number } | null;
  support: { openChats: number; queued: number; slaBreached: number; pendingRequests: number; csat7d: number | null; onlineOperators: number } | null;
  catalog: { moderation: number; active: number; rejected: number; draft: number } | null;
  finance: { payoutsRequested: number; payoutsRequestedAmount: number; fiscalFailed: number; fiscalPending: number; sellerPayable: number } | null;
  hr: { expected: number; present: number; late: number; absent: number; onLeave: number } | null;
  integrations: { total: number; connected: number; error: number } | null;
}

export interface AnalyticsOverview {
  from: string;
  to: string;
  prev: DayRange;
  today: string;
  rollupAt: string | null;
  kpi: {
    orders: KpiValue;
    gmv: KpiValue;
    deliveredShare: KpiValue;
    cancelRate: KpiValue;
    avgCheck: KpiValue;
    activeSellers: KpiValue;
  } | null;
  daily: DailyPoint[] | null;
  channels: ChannelSlice[] | null;
  topSellers: TopSeller[] | null;
  attention: AttentionItem[];
  modules: OverviewModules;
}

export interface TopSku {
  skuId: string;
  productId: string | null;
  title: string;
  code: string | null;
  imageUrl: string | null;
  units: number;
  revenue: number;
  returnedUnits: number;
}

export interface LowStockSku {
  skuId: string;
  title: string;
  code: string | null;
  imageUrl: string | null;
  available: number;
  sold14d: number;
  cover: number | null;
}

export interface SellerOverview {
  from: string;
  to: string;
  prev: DayRange;
  sellerId: string;
  hasOrders: boolean;
  kpi: { gmv: KpiValue; orders: KpiValue; delivered: KpiValue; returned: KpiValue; avgCheck: KpiValue } | null;
  daily: DailyPoint[] | null;
  channels: ChannelSlice[] | null;
  topSkus: TopSku[] | null;
  lowStock: LowStockSku[] | null;
  products: { draft: number; moderation: number; rejected: number; active: number; archived: number; total: number } | null;
  supplies: { inProgress: number; items: { id: string; number: string; status: string; plannedDate: string | null }[] } | null;
  balance: { balance: number; available: number; pending: number; reserved: number; paidTotal: number } | null;
  tickets: { open: number } | null;
  integrations: { total: number; connected: number } | null;
}

export type SalesPoint = SalesTotals & {
  key: string;
  label: string;
  avgCheck: number;
};

export interface SalesReport {
  from: string;
  to: string;
  groupBy: GroupBy;
  sellerId: string | null;
  channel: string | null;
  totals: SalesTotals & { avgCheck: number; deliveredShare: number; cancelRate: number; returnRate: number };
  prevTotals: SalesTotals;
  series: SalesPoint[];
  channels: ChannelSlice[];
  rollupAt: string | null;
}

export interface SellerRankRow {
  sellerId: string;
  name: string;
  logoUrl: string | null;
  orders: number;
  gmv: number;
  units: number;
  delivered: number;
  deliveredPct: number;
  returned: number;
  returnPct: number;
  cancelPct: number;
  avgCheck: number;
  prevGmv: number;
  growth: number | null;
}

export interface SkuRankRow {
  skuId: string;
  sellerId: string;
  sellerName: string | null;
  productId: string | null;
  title: string;
  variantName: string | null;
  code: string | null;
  imageUrl: string | null;
  orderedUnits: number;
  deliveredUnits: number;
  returnedUnits: number;
  revenue: number;
  returnRate: number;
}

export interface SpeedStat {
  count: number;
  medianHours: number | null;
  p90Hours: number | null;
}

export interface OperationsReport {
  from: string;
  to: string;
  speed: {
    confirmedToPacked: SpeedStat;
    packedToDelivered: SpeedStat;
    bins: { key: string; label: string; confirmedToPacked: number; packedToDelivered: number }[];
  } | null;
  byZone: { zone: string; name: string; total: number; delivered: number; failed: number; pct: number }[] | null;
  byCourier: { courierId: string; name: string; total: number; delivered: number; failed: number; pct: number; amount: number }[] | null;
  warehouse: { day: string; packed: number; dispatched: number; returns: number }[] | null;
}

export interface FinanceReport {
  from: string;
  to: string;
  fees: { kind: string; label: string; amount: number; count: number }[] | null;
  feesDaily: { day: string; amount: number }[] | null;
  feesTotal: number | null;
  payables: { owed: number; sellers: number } | null;
  payouts: { requested: { count: number; amount: number }; paid: { count: number; amount: number }; open: { count: number; amount: number } } | null;
  courierCash: { total: number; count: number; daily: { day: string; amount: number }[] } | null;
  receipts: { status: string; count: number; amount: number }[] | null;
}
